import styled from '@emotion/styled';
import React, { memo } from 'react';
import {
	ArrowLinkButton,
	getIconLinkHoverStyle,
	ITheme,
	StyledIconLink,
	useTheme,
	VisuallyHidden,
} from '../../../../../../ui';
import { useSiteMap } from '../../../../../app/pages/ui/utils/withSiteMap';
import { useLocale } from '../../../../../shared/i18n/locale/useLocale';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { IPoi } from '../../../../domain/models/IPoi';
import { ItemContent } from './ItemContent';
import { ItemImage } from './ItemImage';
import { getPoiLinkProps } from './helpers/getPoiLinkProps';
import Responsive from '../../../../../../ui/utils/Responsive';
import themed from '../../../../../../ui/themer/themed';

export const StyledItem = styled.div<{}, ITheme>`
	align-items: center;
	border-bottom: ${themed(({ border }) => border.default)};
	display: flex;
	justify-content: space-between;
	padding: 15px 0;
	width: 100%;

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		cursor: pointer;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: 16px;

		&:last-child {
			border-bottom: ${themed(({ border }) => border.default)};
		}
	}
`;

const StyledItemContent = styled.a`
	display: flex;
	text-align: left;
	width: 100%;

	&:hover {
		text-decoration: none;
		${StyledIconLink} {
			${getIconLinkHoverStyle}
		}
	}
`;

interface IItemProps {
	poi: IPoi;
}

export const Item = memo(({ poi }: IItemProps) => {
	const { t } = useTranslation();
	const currentLocale = useLocale();
	const siteMap = useSiteMap();
	const theme: any = useTheme();
	const isTablet = !!Responsive.matchesBreakpoint('tablet', theme);
	const poiLinkProps = getPoiLinkProps(
		currentLocale,
		poi.poiId,
		isTablet,
		siteMap,
	);

	const titleComponentId = `poi-list-item-title-${poi.poiId}`;

	return (
		<StyledItem>
			<StyledItemContent aria-labelledby={titleComponentId} {...poiLinkProps}>
				<VisuallyHidden as="span">
					{t('web_shop_dine_read_more_sr', {
						name: poi.name,
					})}
				</VisuallyHidden>
				<ItemImage image={poi.image} poi={poi} />
				<ItemContent titleComponentId={titleComponentId} poi={poi} />
			</StyledItemContent>
			{poi.additionalAttributes.grabUrl && (
				<ArrowLinkButton
					href={poi.additionalAttributes.grabUrl}
					hideExternalIndicator
					target="_blank"
					rel="noopener"
				>
					{t('shop_dine_food_order_online_cta')}
				</ArrowLinkButton>
			)}
		</StyledItem>
	);
});
